import Component from '@glimmer/component'
import { action } from '@ember/object'
import type Assignment from 'teacher-ui-client/models/assignment'
import { eq } from 'ember-truth-helpers'
import { fn } from '@ember/helper'
import { on } from '@ember/modifier'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { t } from 'ember-intl'
import optional from 'teacher-ui-client/helpers/optional'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import { AssignmentsTableRow } from 'teacher-ui-client/components/assignments-admin/assignments-table/row'
import { EditAssignment } from 'teacher-ui-client/components/assignments-admin/edit-assignment'
import { InnerStudentReviewsTable } from 'teacher-ui-client/components/assignments-admin/assignments-table/inner-student-reviews-table'
import { InnerStudentScoresTable } from 'teacher-ui-client/components/assignments-admin/assignments-table/inner-student-scores-table'
import { colspanMax } from 'teacher-ui-client/modifiers/colspan-max'
import { Tooltip } from 'teacher-ui-client/components/tooltip'
import type { AssignmentScoresData } from 'teacher-ui-client/utils/student-events/fetchers/assignment-scores'

interface Signature {
  Args: {
    assignments: Assignment[]
    assignmentScores: AssignmentScoresData
    selectedAssignment?: Assignment
    subscriptionType: SubscriptionType
    currentSortDirection: string
    currentSortColumn: string
    sortColumnAction?: (sortCol: string, sortDirection: string) => unknown
    showAssignmentAction?: (id: string) => unknown
    editAssignmentAction?: (id: string) => unknown
    closeAssignmentAction?: () => unknown
    showEditAndExpireAssignment?: boolean
    openMode?: 'show' | 'edit'
  }
}

/**
 * This component renders a sortable list of assignments and student results
 */
export class AssignmentsTable extends Component<Signature> {
  get assignmentRows() {
    return this.args.assignments.map((assignment) => {
      const scoreItem = this.args.assignmentScores.find((scoreItem) => scoreItem.assignment_uuid === assignment.id)
      return {
        assignment,
        averageScore: scoreItem?.average_score,
        scoresForAssignment: scoreItem?.students ?? [],
      }
    })
  }

  /**
   * Given that we only show 1 product of assignments in a table, we can use this to know whether to show the review
   * columns.
   */
  get showReviews() {
    return this.args.assignments.some((a) => a.product === 'rex')
  }

  /**
   * Sorts the table based on a column by calling the assignments endpoint.
   * Updates the internal state of the sort column and direction by
   * updating the queryParams and data, if required.
   */
  @action
  sortColumn(columnToSort: string) {
    const sortDirection = this.args.currentSortDirection === 'asc' ? 'desc' : 'asc'
    this.args.sortColumnAction?.(columnToSort, sortDirection)
  }

  get headerClass() {
    switch (this.args.subscriptionType) {
      case SubscriptionType.Reading:
        return 'bg-oceany-blue-100'
      case SubscriptionType.Maths:
        return 'bg-ms-green-100'
      case SubscriptionType.Writing:
        return 'bg-wl-blue-100'
      default:
        return ''
    }
  }

  <template>
    <table class="w-full divide-y divide-white overflow-auto" data-test-assignment-table>
      <thead>
        <tr class="{{this.headerClass}} divide-x divide-white">
          {{! need this empty th for the button that toggles the students table }}
          <th class="w-0 border-r-0 px-3 py-4 print:hidden"></th>
          <th class="min-w-72 px-3 py-4" data-heading="Assignment title">
            {{t "assignments.headings.title"}}
          </th>
          <th data-heading="Assigned by" class="px-3 py-4">
            {{t "assignments.headings.assignedBy"}}
          </th>
          <th
            data-heading="Start date"
            class="{{if (eq @currentSortColumn 'start_at') 'text-dusty-black-500'}} px-3 py-4"
            {{on "click" (fn this.sortColumn "start_at")}}
            role="button"
          >
            <div class="flex flex-nowrap items-center gap-4">
              <span> {{t "assignments.headings.startDate"}}</span>
              <PhosphorIcon
                @icon={{if
                  (eq @currentSortColumn "start_at")
                  (if (eq @currentSortDirection "asc") "phosphor-caret-up-fill" "phosphor-caret-down-fill")
                  "phosphor-caret-up-down-fill"
                }}
                role="presentation"
              />
            </div>
          </th>
          <th
            data-heading="Due date"
            class="{{if (eq @currentSortColumn 'end_at') 'text-dusty-black-500'}} px-3 py-4"
            {{on "click" (fn this.sortColumn "end_at")}}
            role="button"
          >
            <div class="flex flex-nowrap items-center gap-4">
              <span>{{t "assignments.headings.dueDate"}}</span>
              <PhosphorIcon
                @icon={{if
                  (eq @currentSortColumn "end_at")
                  (if (eq @currentSortDirection "asc") "phosphor-caret-up-fill" "phosphor-caret-down-fill")
                  "phosphor-caret-up-down-fill"
                }}
                role="presentation"
              />
            </div>
          </th>
          {{! NOTE: even though the assignment model & payload provide assignment.status, the backend only sorts if the sort key here is 'state' }}
          <th
            data-heading="Status"
            class="{{if (eq @currentSortColumn 'state') 'text-dusty-black-500'}} px-3 py-4"
            {{on "click" (fn this.sortColumn "state")}}
            role="button"
          >
            <div class="flex flex-nowrap items-center gap-4">
              <span>{{t "assignments.headings.status"}}</span>
              <PhosphorIcon
                @icon={{if
                  (eq @currentSortColumn "state")
                  (if (eq @currentSortDirection "asc") "phosphor-caret-up-fill" "phosphor-caret-down-fill")
                  "phosphor-caret-up-down-fill"
                }}
                role="presentation"
              />
            </div>
          </th>
          <th class="px-3 py-4" data-heading="Students completed">
            <span>
              {{t "assignments.headings.studentsCompleted"}}
            </span>
          </th>
          <th class="px-3 py-4" data-heading="Av. Score %">
            <span>{{t "assignments.headings.averageScoreAsPercentage" htmlSafe=true}}</span>
          </th>
          {{#if this.showReviews}}
            <th class="flex h-full w-full items-center gap-x-3 px-3 py-4">
              <span>{{t "assignments.headings.assignmentsReviewed"}}</span>
              <span class="print:hidden">
                <PhosphorIcon @icon="phosphor-info-fill" class="text-dusty-black-300 h-5 w-5 cursor-pointer" />
                <Tooltip @text={{t "assignments.headings.assignmentsReviewedTooltip"}} />
              </span>
            </th>
          {{/if}}
          <th class="px-3 py-4 print:hidden"></th>
        </tr>
      </thead>
      <tbody class="divide-dusty-black-100 divide-y">
        {{#each this.assignmentRows as |assignmentRow|}}
          <AssignmentsTableRow
            data-test-assignment-row={{assignmentRow.assignment.id}}
            @assignment={{assignmentRow.assignment}}
            @averageScore={{assignmentRow.averageScore}}
            @showReviews={{this.showReviews}}
            @showEditAndExpireAssignment={{@showEditAndExpireAssignment}}
            @subscriptionType={{@subscriptionType}}
            @showAssignmentAction={{optional @showAssignmentAction}}
            @editAssignmentAction={{optional @editAssignmentAction}}
            @closeAssignmentAction={{optional @closeAssignmentAction}}
            @openMode={{@openMode}}
            @selectedAssignmentId={{@selectedAssignment.id}}
          />
          {{#if @selectedAssignment}}
            {{#if (eq assignmentRow.assignment.id @selectedAssignment.id)}}
              <tr class="print:hidden">
                <td class="p-0" {{colspanMax}}>
                  {{#if (eq @openMode "edit")}}
                    <EditAssignment
                      class="border-t border-white"
                      @assignment={{assignmentRow.assignment}}
                      @closeAssignment={{@closeAssignmentAction}}
                    />
                  {{else if (eq @openMode "show")}}
                    {{#if assignmentRow.assignment.isReadAloudAssignment}}
                      <InnerStudentReviewsTable
                        class="border-t border-white"
                        @assignment={{assignmentRow.assignment}}
                      />
                    {{else}}
                      <InnerStudentScoresTable
                        class="border-t border-white"
                        @assignment={{assignmentRow.assignment}}
                        @scoresForAssignment={{assignmentRow.scoresForAssignment}}
                      />
                    {{/if}}
                  {{/if}}
                </td>
              </tr>
            {{/if}}
          {{/if}}
        {{else}}
          <tr class="print:hidden">
            <td class="p-3" {{colspanMax}}>
              <div class="missing-assignments-data">
                {{t "assignments.missingAssignmentsDataInList"}}
              </div>
            </td>
          </tr>
        {{/each}}
      </tbody>
    </table>
  </template>
}

export default AssignmentsTable
